/**
 * What an account may do, read off the session rather than worked out from the
 * role.
 *
 * The server sends the list. Deriving it here from the role would be a second
 * copy of the rule, and the day the two disagree the console shows a control
 * that is refused — or hides one that is allowed, which is worse, because
 * nobody reports a button they never saw.
 *
 * Hiding is all this does. The server refuses regardless, and every screen that
 * uses these still handles a refusal.
 */

import type { AccountSession, Capability } from '../api/types';

/** Whether this session carries a capability at all, before any question of which tenancy. */
export function can(session: AccountSession | null, capability: Capability): boolean {
  return session !== null && session.capabilities.includes(capability);
}

/**
 * Whether this account may choose its tenancy's identity provider.
 *
 * Its own function because it is the one capability a provider operator does
 * **not** carry, and the reason is worth having in one place: somebody who
 * could point a customer's cluster at an identity provider could point it at
 * one whose tokens they mint, and then sign in to that customer's cluster. The
 * server withholds `choose_identity_provider` from `provider_admin` for exactly
 * that reason, and `provider_support` reads and changes nothing at all.
 */
export function mayChooseIdentityProvider(session: AccountSession | null): boolean {
  return can(session, 'choose_identity_provider');
}

/**
 * Whether this account may move tenancies to another gdsgate version.
 *
 * Its own function for the same reason as the one above: it is the capability
 * whose holder is not the obvious one. Reading the version board takes
 * `view_tenant` — every role has it, and a customer's representative is meant to
 * see what their own cluster runs — while everything that *moves* anything takes
 * this, which the provider's operator alone carries. Support reads and signs
 * nothing off; a tenancy cannot pin its own version, because signing a wave off
 * decides when everybody else moves.
 *
 * Gated on the capability and never on the name of the role: the server sends
 * the list, and a console that worked it out from `provider_admin` would be a
 * second copy of the rule, due to disagree.
 */
export function mayRollOutVersion(session: AccountSession | null): boolean {
  return can(session, 'roll_out_version');
}
